// Local-mode mocks for what decommissioning leaves behind:
// POST /instances/decommissionPreview and /instances/keptPasswords, plus the
// helpers the /instances/decommission mock (mockData.js) answers with.
//
// Endpoint 1 runs through authnull-svc with 4 open grants and two rotated
// passwords (deploy's rotation unconfirmed); endpoint 5 was set up through an
// existing admin account ("sysadmin"), no grants open, and keeps two rotated
// passwords. Every other endpoint can't be cleaned up automatically and keeps
// none.
//
// Registered at the FRONT of findMockResponse: "/instances/decommissionpreview"
// must match before the "/instances/decommission" pattern does.

import { SSH_PROXY_MOCKS } from "./sshProxyMocks";

const ago = (ms) => new Date(Date.now() - ms).toISOString();
const DAY = 24 * 3600e3;

const rowOf = (id) => (SSH_PROXY_MOCKS["/instances/list"]?.instances || []).find((r) => r.instanceId === Number(id));

// instanceId → { account, rotatedAt, unconfirmed }[]
const KEPT = {
  1: [
    { account: "deploy", rotatedAt: ago(2 * DAY), unconfirmed: true },
    { account: "ubuntu", rotatedAt: ago(6 * DAY), unconfirmed: false },
  ],
  5: [
    { account: "app", rotatedAt: ago(3 * DAY), unconfirmed: false },
    { account: "postgres", rotatedAt: ago(12 * DAY), unconfirmed: false },
  ],
};

const COMMON_FILES = [
  "/usr/local/sbin/authnull-svc-rotate",
  "/etc/ssh/authnull",
  "/etc/ssh/sshd_config.d/00-authnull-grant-keys.conf",
  "/etc/ssh/sshd_config.d/01-authnull-password-ssh.conf",
  "/etc/sudoers.d/authnull-sudo-*",
];

const accessModeOf = (id) => (Number(id) === 5 ? "admin" : "svc");

// The same list the server's cleanupFiles(accessMode) returns.
export function mockCleanupFiles(instanceId) {
  if (accessModeOf(instanceId) === "admin") return [...COMMON_FILES];
  return [...COMMON_FILES, "/etc/ssh/sshd_config.d/61-authnull-svc.conf", "/etc/sudoers.d/authnull-svc", "the authnull-svc account"];
}

export const mockKeptPasswords = (instanceId) => (KEPT[Number(instanceId)] || []).map((k) => ({ ...k }));

// The decommission answer's passwordsKept: account names only.
export const mockPasswordsKept = (instanceId) => mockKeptPasswords(instanceId).map((k) => k.account);

export const DECOMMISSION_MOCKS = [
  {
    pattern: "/instances/decommissionpreview",
    respond: (body = {}) => {
      const id = Number(body?.instanceId);
      const row = rowOf(id);
      const base = {
        status: row?.status || "Active",
        hostname: row?.hostName || row?.privateIp || `endpoint-${id}`,
        cleanupFiles: mockCleanupFiles(id),
        keptPasswords: mockKeptPasswords(id),
      };
      if (id === 1) return { ...base, accessMode: "svc", activeGrants: 4, selfCleanup: true };
      if (id === 5) return { ...base, accessMode: "admin", adminAccount: "sysadmin", activeGrants: 0, selfCleanup: true };
      return { ...base, accessMode: "svc", activeGrants: 1, selfCleanup: false };
    },
  },
  {
    pattern: "/instances/keptpasswords",
    respond: (body = {}) => {
      const row = rowOf(body?.instanceId);
      return { status: row?.status || "", passwords: mockKeptPasswords(body?.instanceId) };
    },
  },
];
